import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { Button, DialogContent, Skeleton, Tab, Tabs, Theme, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import useDashboardAccount from '../../hooks/useDashboardAccount';
import DialogAdaptive from '../common/DialogAdaptive';
import StyledCardTitleBar from '../common/StyledCardTitleBar';
import StyledDialogActions from '../common/StyledDialogActions';
import ProfileDialogAccount from './ProfileDialogAccount';
import ProfileDialogBillingDetails from './ProfileDialogBillingDetails';
import ProfileDialogChangePassword from './ProfileDialogChangePassword';

type CustomerProfileFormProps = {
    open: boolean;
    handleClose: () => void;
    preSelectTab?: number;
};

const ProfileDialog: React.FC<CustomerProfileFormProps> = ({ open, handleClose, preSelectTab = 0 }) => {
    const { dashboardAccount, dashboardAccountLoading, dashboardAccountSave, dashboardAccountIsSaveing } = useDashboardAccount(open);
    const theme: Theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const [tab, setTab] = useState(preSelectTab);

    const [timezone, setTimezone] = useState<string>(moment.tz.guess());

    const [billingName, setBillingName] = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const [billingCity, setBillingCity] = useState('');
    const [billingCounty, setBillingCounty] = useState('');
    const [billingPostcode, setBillingPostcode] = useState('');
    const [billingCountry, setBillingCountry] = useState('');
    const [billingIsCompany, setBillingIsCompany] = useState(false);
    const [billingCompanyName, setBillingCompanyName] = useState('');
    const [billingVatNumber, setBillingVatNumber] = useState('');
    const [isCompanyVatNumberValid, setIsCompanyVatNumberValid] = useState(false);

    // Reset tab to 0 when dialog is opened
    useEffect(() => {
        setTab(0);
    }, [open]);

    // Load account data when dashboard data is loaded
    useEffect(() => {
        if (dashboardAccount && dashboardAccount.timezone) {
            setTimezone(dashboardAccount.timezone);
        }

        const { financial } = dashboardAccount || {};

        setBillingName((financial && financial.billingName) || '');
        setBillingAddress((financial && financial.billingAddress) || '');
        setBillingCity((financial && financial.billingCity) || '');
        setBillingCounty((financial && financial.billingCounty) || '');
        setBillingPostcode((financial && financial.billingPostcode) || '');
        setBillingCountry((financial && financial.billingCountry) || '');
        setBillingIsCompany((financial && financial.billingIsCompany) || false);
        setBillingCompanyName((financial && financial.billingCompanyName) || '');
        setBillingVatNumber((financial && financial.billingVatNumber) || '');
    }, [dashboardAccount]);

    const handleSubmit = () => {
        // Handle form submission
        dashboardAccountSave(
            {
                timezone,
                financial: {
                    billingName: billingName.trim(),
                    billingAddress: billingAddress.trim(),
                    billingCity: billingCity.trim(),
                    billingCounty: billingCounty.trim(),
                    billingPostcode: billingPostcode.trim(),
                    billingCountry: billingCountry,
                    billingIsCompany: billingIsCompany,
                    billingCompanyName: billingCompanyName.trim(),
                    billingVatNumber: billingVatNumber.length > 0 ? `${billingCountry}${billingVatNumber}` : '',
                },
            },
            handleClose,
        ).then(data => {
            if (data) {
                enqueueSnackbar('Profile updated successfully', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            }
        });
    };

    return (
        <DialogAdaptive maxWidth={'sm'} fullWidth open={open} onClose={handleClose}>
            <StyledCardTitleBar
                title={
                    <Tabs
                        value={tab}
                        onChange={(e, newValue) => setTab(newValue)}
                        variant="scrollable"
                        sx={{
                            // Text color for the tabs
                            '& .MuiTab-root': {
                                color: theme.palette.grey[500],
                            },
                            // Text color for the selected tab
                            '& .Mui-selected': {
                                color: `${theme.palette.common.white} !important`,
                            },
                            // Background for the selected tab
                            '& .MuiTabs-indicator': {
                                backgroundColor: theme.palette.secondary.main,
                            },
                        }}
                    >
                        <Tab label="Account" value={0} disableRipple />
                        <Tab label="Billing Details" value={1} disableRipple />
                        <Tab label="Change Password" value={2} disableRipple />
                    </Tabs>
                }
            ></StyledCardTitleBar>
            <DialogContent>
                {dashboardAccountLoading && <Skeleton />}

                {tab === 0 && <ProfileDialogAccount timezone={timezone} setTimezone={setTimezone} />}
                {tab === 1 && (
                    <ProfileDialogBillingDetails
                        name={billingName}
                        setName={setBillingName}
                        address={billingAddress}
                        setAddress={setBillingAddress}
                        city={billingCity}
                        setCity={setBillingCity}
                        county={billingCounty}
                        setCounty={setBillingCounty}
                        postcode={billingPostcode}
                        setPostcode={setBillingPostcode}
                        country={billingCountry}
                        setCountry={setBillingCountry}
                        isCompany={billingIsCompany}
                        setIsCompany={setBillingIsCompany}
                        companyName={billingCompanyName}
                        setCompanyName={setBillingCompanyName}
                        companyVatNumber={billingVatNumber}
                        setCompanyVatNumber={setBillingVatNumber}
                        isCompanyVatNumberValid={isCompanyVatNumberValid}
                        setIsCompanyVatNumberValid={setIsCompanyVatNumberValid}
                    />
                )}
                {tab === 2 && <ProfileDialogChangePassword />}
            </DialogContent>
            <StyledDialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                {tab !== 2 && (
                    <LoadingButton
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        loading={dashboardAccountIsSaveing}
                        disabled={billingVatNumber.length > 0 && !isCompanyVatNumberValid}
                    >
                        <span>Save</span>
                    </LoadingButton>
                )}
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default ProfileDialog;
